import React, { useEffect, useState } from "react";
import { ColumnProps } from "antd/lib/table";
import { Button } from "antd";
import { EwaybillConsumedMonthlyStatistic, getMonthlyStatistics } from "@/services/api/details";
import {
  SharedEwaybillAccount,
  SharedEwaybillBranchAccount,
  SharedEwaybillShippingAddress,
  SharedEwaybillShippingAddressStatus,
  SharedEwaybillSubscription,
} from "@/services/api/sharedEwaybillAccount";
import { Link, useHistory } from "react-router-dom";
import SharedEwaybillAccountTypeNameMap from "@/map/SharedEwaybillAccountTypeNameMap";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import moment from "moment";

interface UseConsumerTableColumnsProps {
  ewaybillShippingAddressMapping?: Map<number, SharedEwaybillShippingAddress>;
  ewaybillAccountMapping?: Map<number, SharedEwaybillAccount>;
  branchAccountMapping?: Map<number, SharedEwaybillBranchAccount>;
  subscriptionMapping?: Map<number, SharedEwaybillSubscription>;

  data?: SharedEwaybillShippingAddress[];

  reload?(): void;
}

function useConsumerTableColumns(props: UseConsumerTableColumnsProps): ColumnProps<SharedEwaybillShippingAddress>[] {
  const { ewaybillAccountMapping, branchAccountMapping, subscriptionMapping, data: list, reload } = props;

  const [statisticsColumns, setStatisticsColumns] = useState<ColumnProps<SharedEwaybillShippingAddress>[]>([
    { title: "加载中", width: 70 },
    { title: "加载中", width: 70 },
    { title: "加载中", width: 70 },
  ]);

  const history = useHistory();

  useEffect(() => {
    (async () => {
      const sharedEwaybillShippingAddressIds: number[] = [];

      list?.forEach((it) => {
        if (it?.id) {
          sharedEwaybillShippingAddressIds.push(it?.id);
        }
      });

      if (sharedEwaybillShippingAddressIds.length) {
        const data = await getMonthlyStatistics(sharedEwaybillShippingAddressIds);

        /**
         * `year-month-shippingAddressId` -> EwaybillConsumedMonthlyStatistic 索引
         */
        const shippingAddressYearMonthMapping = new Map<string, EwaybillConsumedMonthlyStatistic>();
        const yearMonths = [];
        data.forEach((it) => {
          const yearMonth = `${it.year}-${it.month.toString().padStart(2, "0")}`;
          const shippingAddressYearMonth = `${yearMonth}-${it.sharedEwaybillShippingAddressId}`;
          if (!yearMonths.includes(yearMonth)) {
            yearMonths.push(yearMonth);
          }
          shippingAddressYearMonthMapping.set(shippingAddressYearMonth, it);
        });

        yearMonths.sort((a, b) => {
          return moment(a).unix() - moment(b).unix();
        });

        const newStatisticsColumns: ColumnProps<any>[] = yearMonths.map((yearMonth) => {
          return {
            key: yearMonth,
            title: yearMonth,
            width: 150,
            render(_, shippingAddress) {
              const id = shippingAddress.id;
              const shippingAddressYearMonth = `${yearMonth}-${id}`;
              const statistic = shippingAddressYearMonthMapping.get(shippingAddressYearMonth);

              if (!statistic) {
                return null;
              }
              return (
                <div style={{ fontSize: 11, color: "black" }}>
                  <div>已用：{statistic.allocatedQuantity}</div>
                  <div>已取消：{statistic.cancelQuantity}</div>
                </div>
              );
            },
          };
        });

        setStatisticsColumns(newStatisticsColumns);
      }
    })();
  }, [props.data]);

  return [
    {
      title: "面单来源账号",
      key: "platformUsername",
      width: 200,
      render: (_, shippingAddress) => {
        const account = ewaybillAccountMapping.get(shippingAddress.id);
        const platformUsername = account?.platformUsername;
        const type = account?.type;

        const ewaybillTypeName = SharedEwaybillAccountTypeNameMap[type];

        return (
          <>
            <div className="text-small">
              <div>{platformUsername}</div>
              <div>{ewaybillTypeName}</div>
            </div>
          </>
        );
      },
    },
    {
      title: "快递公司/网点/发货地址",
      key: "logisticsCompany",
      width: 400,
      render: (_, shippingAddress) => {
        const subscription = subscriptionMapping.get(shippingAddress.id);
        const branchAccount = branchAccountMapping.get(shippingAddress.id);
        const { province, city, district, town, street } = shippingAddress;

        const logisticsCompany = subscription?.logisticsCompany;
        const logisticsCompanyName = LogisticsCompanyNameMap[logisticsCompany];

        const branchAccountNameArr = [];
        if (branchAccount.branchName) {
          branchAccountNameArr.push(branchAccount.branchName);
        }
        if (branchAccount.branchCode) {
          branchAccountNameArr.push(`(${branchAccount.branchCode})`);
        }
        if (branchAccount.waybillNoTypeName) {
          branchAccountNameArr.push(`-${branchAccount.waybillNoTypeName}`);
        }

        const branchAccountName = branchAccountNameArr.join("");
        const shippingAddressName = [province, city, district, town, street].filter((it) => it).join("");

        return (
          <div className="text-small">{[logisticsCompanyName, branchAccountName, shippingAddressName].join("/")}</div>
        );
      },
    },
    {
      title: "共享面单的人",
      key: "providerCompanyName",
      width: 150,
      render: (_, shippingAddress) => {
        const account = ewaybillAccountMapping.get(shippingAddress.id);
        const providerCompanyName = account?.providerCompanyName;

        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div className={"attentionColor"}>微信：{providerCompanyName}</div>
          </div>
        );
      },
    },
    {
      title: "账户信息",
      key: "used",
      width: 180,
      render: (_, shippingAddress) => {
        const { allocatedQuantity, cancelQuantity, quantity } = shippingAddress;

        const ewaybillAccount = ewaybillAccountMapping.get(shippingAddress.id);

        const to = `/shareEwaybillToMe/details/${ewaybillAccount.providerCompanyId}/${ewaybillAccount.ewaybillAccountId}/${ewaybillAccount.consumerCompanyId}`;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <div style={{ minWidth: 80 }} className="tl">
              <div>余额：{quantity}</div>
              <div>已用：{allocatedQuantity}</div>
              <div>已取消：{cancelQuantity}</div>
            </div>
            <Link to={to}>明细</Link>
          </div>
        );
      },
    },
    ...statisticsColumns,
    {
      title: "状态",
      width: 180,
      key: "status",
      render(_, shippingAddress) {
        if (shippingAddress.status === SharedEwaybillShippingAddressStatus.DELETED) {
          return (
            <span className="text-small">
              <span
                style={{ display: "inline-block", width: 7, height: 7, borderRadius: "100%", background: "#F5222D" }}
              ></span>
              <span className="ml5">已删除</span>
            </span>
          );
        }

        if (shippingAddress.status === SharedEwaybillShippingAddressStatus.DISABLE) {
          return (
            <span className="text-small">
              <span
                style={{ display: "inline-block", width: 7, height: 7, borderRadius: "100%", background: "#BFBFBF" }}
              ></span>
              <span className="ml5">暂停</span>
            </span>
          );
        }

        if (shippingAddress.status === SharedEwaybillShippingAddressStatus.ENABLE) {
          return (
            <span className="text-small">
              <span
                style={{ display: "inline-block", width: 7, height: 7, borderRadius: "100%", background: "#52C41A" }}
              ></span>
              <span className="ml5">共享中</span>
            </span>
          );
        }

        return null;
      },
    },
    {
      title: "操作",
      key: "operation",
      width: 150,
      render: (_, shippingAddress) => {
        const { id } = shippingAddress;
        const account = ewaybillAccountMapping.get(shippingAddress.id);
        const consumerCompanyName = account?.consumerCompanyName;

        return (
          <div>
            <Button
              type="link"
              size="small"
              onClick={() => {
                const { province, city, district, town, street } = shippingAddress;
                const account = ewaybillAccountMapping.get(shippingAddress.id);
                const subscription = subscriptionMapping.get(shippingAddress.id);
                const branchAccount = branchAccountMapping.get(shippingAddress.id);
                const platformUsername = account?.platformUsername;
                const type = account?.type;

                const ewaybillTypeName = SharedEwaybillAccountTypeNameMap[type];
                const logisticsCompanyName = LogisticsCompanyNameMap[subscription.logisticsCompany];
                const branchCode = branchAccount.branchCode;
                const joinedShippingAddress = [province, city, district, town, street].filter((it) => it).join("");

                const title = [
                  platformUsername,
                  `(${ewaybillTypeName})`,
                  logisticsCompanyName,
                  branchCode,
                  joinedShippingAddress,
                ].join("/");

                const search = new URLSearchParams();

                search.set(
                  "extraInfo",
                  JSON.stringify({
                    title: title,
                    providerCompanyName: consumerCompanyName,
                  })
                );

                history.push(`/shareEwaybillToMe/operationRecord/${id}?${search.toString()}`);
              }}
            >
              充值记录
            </Button>
          </div>
        );
      },
    },
  ];
}

export default useConsumerTableColumns;
